/**
 * Shared utility functions for Admin components
 */

/**
 * Format money value to USD string
 * @param {number} amount - The amount to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted money string
 */
export const formatMoney = (amount, decimals = 2) => {
  if (amount === null || amount === undefined) return '$0.00';
  return `$${Number(amount).toFixed(decimals)}`;
};

/**
 * Format date/time string
 * @param {string} dateStr - ISO date string
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateTime = (dateStr, options = {}) => {
  if (!dateStr) return 'N/A';
  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  return new Date(dateStr).toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

/**
 * Format date only (no time)
 * @param {string} dateStr - ISO date string
 * @returns {string} Formatted date string
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Get human-readable status label from status code
 * @param {string} status - Status code (e.g., 'pending_deposit', 'completed_by_vendor')
 * @returns {string} Human-readable label
 */
export const getStatusLabel = (status) => {
  if (!status) return 'Unknown';
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Get status badge CSS class based on status
 * @param {string} status - Status code
 * @param {string} type - Type of status ('booking', 'payment', 'user', 'dispute')
 * @returns {string} CSS class string
 */
export const getStatusBadgeClass = (status, type = 'booking') => {
  if (type === 'payment') {
    if (status === 'succeeded') return 'bg-green-100 text-green-800';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-800';
    if (status === 'failed') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  }
  
  if (type === 'booking') {
    if (status === 'completed') return 'bg-green-100 text-green-800';
    if (status === 'completed_by_vendor') return 'bg-blue-100 text-blue-800';
    if (status === 'final_paid') return 'bg-emerald-100 text-emerald-800';
    if (status === 'deposit_paid') return 'bg-cyan-100 text-cyan-800';
    if (status === 'pending_deposit') return 'bg-yellow-100 text-yellow-800';
    if (status?.includes('cancelled')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  }
  
  if (type === 'user') {
    if (status === 'active') return 'bg-green-100 text-green-800';
    if (status === 'suspended') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  }
  
  if (type === 'dispute') {
    if (status === 'resolved') return 'bg-green-100 text-green-800';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-800';
    if (status === 'under_review') return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  }
  
  return 'bg-gray-100 text-gray-800';
};

/**
 * Calculate platform fee from amount
 * @param {number} amount - Total amount
 * @param {number} feePercentage - Fee percentage (default: 5)
 * @returns {number} Platform fee amount
 */
export const calcPlatformFee = (amount, feePercentage = 5) => {
  if (!amount || isNaN(amount)) return 0;
  return (amount * feePercentage) / 100;
};

/**
 * Calculate vendor payout (amount minus platform fee)
 * @param {number} amount - Total amount
 * @param {number} feePercentage - Fee percentage (default: 5)
 * @returns {number} Vendor payout amount
 */
export const calcVendorPayout = (amount, feePercentage = 5) => {
  if (!amount || isNaN(amount)) return 0;
  return amount - calcPlatformFee(amount, feePercentage);
};

/**
 * Get payment status display text
 * @param {string} status - Raw status value
 * @returns {string} Display text
 */
export const getPaymentStatusDisplay = (status) => {
  if (status === 'succeeded') return 'Completed';
  if (status === 'pending') return 'Processing';
  if (status === 'failed') return 'Failed';
  return 'Processing';
};

/**
 * Truncate ID for display
 * @param {string} id - Full ID string
 * @param {number} length - Number of characters to show (default: 12)
 * @returns {string} Truncated ID with ellipsis
 */
export const truncateId = (id, length = 12) => {
  if (!id) return 'N/A';
  if (id.length <= length) return id;
  return `${id.slice(0, length)}...`;
};
